import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Eye, DollarSign, MessageSquare, Send, CalendarPlus, X, User, Calendar, ClipboardList, Check, Tag, Image, History, AlertTriangle } from 'lucide-react';
import NavigationBar from '../components/NavigationBar';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import { InfoTooltip } from '../components/InfoTooltip';
import LoadingSpinner from '../components/LoadingSpinner';
import DepositPaymentDetailsModal from '../components/DepositPaymentDetailsModal';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { apiService } from '../services/api';
import { QuoteRow, QuoteConversationMessage } from '../types';
import { shouldShowBusinessDashboard } from '../utils/navigationUtils';
import { canViewFullCustomerPhone } from '../utils/roleUtils';
import { displayCustomerPhone } from '../utils/phoneFormatUtils';
import { getQuoteDisplayStatus } from '../utils/quoteDisplayStatus';
import { renderQuoteDepositReceivedEventText } from '../utils/quoteEventDisplay';
import toast from 'react-hot-toast';

const formatRequestNumber = (id: string) => (id ? `Q-${id.slice(0, 8)}` : '—');
const formatDateOnly = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};
const formatTimeOnly = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const formatSchedule = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const formatDate = (v: string) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const formatQuoteEventActor = (firstName?: string | null, lastName?: string | null) => {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  return name || 'System';
};
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const getOfferExpirationDaysInput = (days: number | null | undefined, expiresAt?: string | null) => {
  if (days != null && Number.isInteger(days) && days >= 1) return String(days);
  const v = expiresAt;
  if (!v) return '1';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '1';
  const diffMs = d.getTime() - Date.now();
  if (diffMs <= 0) return '1';
  return String(Math.max(1, Math.ceil(diffMs / ONE_DAY_MS)));
};
const getOfferExpirationIsoFromDays = (days: number) => new Date(Date.now() + (days * ONE_DAY_MS)).toISOString();
const formatMoney = (n: number | null | undefined) =>
  n != null && Number.isFinite(n) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) : '—';
const OFFER_CARD_TOOLTIP_TEXT = 'Set the total offer, optional deposit, expiration, and customer note. After the first send, this becomes an update flow so you can revise and resend the offer if the customer requests changes.';
const TOTAL_OFFER_TOOLTIP_TEXT = 'This is the full service price before any deposit is applied.';
const REQUIRED_DEPOSIT_TOOLTIP_TEXT = 'Once the offer is accepted, a draft appointment will be created. The customer must pay the specified deposit amount to confirm the appointment.';
const OFFER_EXPIRATION_TOOLTIP_TEXT = 'Default is 1 day. Change the number of days to control how long the customer has to accept before the offer expires.';
const CUSTOMER_WILL_SEE_TOOLTIP_TEXT = 'This section reflects exactly what the customer will see in the offer summary.';
const MESSAGE_TO_CUSTOMER_TOOLTIP_TEXT = 'Optional short note included with the offer. Good for timing, scope, or anything the customer should know before accepting.';
const CONVERSATION_TOOLTIP_TEXT = 'Keep the conversation close to the offer so the business can clarify scope, then send or update the offer without leaving this column.';

const QuoteRequestDetailsPage: React.FC = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  const [quote, setQuote] = useState<QuoteRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [offerAmountInput, setOfferAmountInput] = useState<string>('');
  const [depositAmountInput, setDepositAmountInput] = useState<string>('');
  const [offerExpirationDaysInput, setOfferExpirationDaysInput] = useState<string>('1');
  const [requireDeposit, setRequireDeposit] = useState(true);
  const [offerMessage, setOfferMessage] = useState('');
  const [conversationInput, setConversationInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState<{
    scheduledAt: string | null;
    flexScheduledAt?: { earliest: string; latest: string } | null;
    status?: string | null;
  } | null>(null);
  const [isCreateAppointmentModalOpen, setIsCreateAppointmentModalOpen] = useState(false); // kept for compatibility; Create Appointment now uses dedicated page
  const [presignedImageUrls, setPresignedImageUrls] = useState<string[] | null>(null);
  const [presignedImageUrlsLoading, setPresignedImageUrlsLoading] = useState(false);
  const [loadedImageIndices, setLoadedImageIndices] = useState<Set<number>>(new Set());
  const [lightboxImageUrl, setLightboxImageUrl] = useState<string | null>(null);
  const [onlinePaymentsEnabled, setOnlinePaymentsEnabled] = useState(false);
  const [isDepositPaymentDetailsModalOpen, setIsDepositPaymentDetailsModalOpen] = useState(false);

  const loadQuote = useCallback(async () => {
    if (!quoteId) return;
    try {
      setLoading(true);
      const data = await apiService.getQuote(quoteId);
      setQuote(data);
      const offerFromQuote =
        data.businessOfferAmount != null && Number.isFinite(data.businessOfferAmount)
          ? Number(data.businessOfferAmount).toFixed(2)
          : '';
      setOfferAmountInput(offerFromQuote);
      const depositFromQuote =
        data.requiredDepositAmount != null && Number.isFinite(data.requiredDepositAmount)
          ? Number(data.requiredDepositAmount).toFixed(2)
          : '';
      setDepositAmountInput(depositFromQuote);
      setOfferExpirationDaysInput(getOfferExpirationDaysInput(data.offerExpirationDays, data.offerExpiredAt));
      setRequireDeposit(depositFromQuote !== '' && Number(parseFloat(depositFromQuote)) > 0);
      // When quote has no deposit set, prefill from offer settings only if "require deposit by default" is on
      if (depositFromQuote === '' && (data.businessId || currentBusiness?.id)) {
        const businessId = data.businessId || currentBusiness?.id;
        if (businessId) {
          try {
            const offerSettings = await apiService.getOfferSettings(businessId);
            if (
              offerSettings.requireDepositByDefault === true &&
              offerSettings.defaultDepositAmount != null &&
              Number.isFinite(offerSettings.defaultDepositAmount) &&
              offerSettings.defaultDepositAmount > 0
            ) {
              setDepositAmountInput(String(offerSettings.defaultDepositAmount));
              setRequireDeposit(true);
            }
          } catch {
            // ignore; keep deposit empty if settings fail to load
          }
        }
      }
    } catch (err) {
      console.error('Error loading quote:', err);
      toast.error('Failed to load quote request');
      setQuote(null);
    } finally {
      setLoading(false);
    }
  }, [quoteId, currentBusiness?.id]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  useEffect(() => {
    if (!currentBusiness?.id) {
      setOnlinePaymentsEnabled(false);
      return;
    }
    let cancelled = false;
    apiService
      .getBusinessStripeAccount(currentBusiness.id)
      .then((data) => {
        if (!cancelled) setOnlinePaymentsEnabled(!!data?.chargesEnabled);
      })
      .catch(() => {
        if (!cancelled) setOnlinePaymentsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentBusiness?.id]);

  useEffect(() => {
    if (!quote?.appointmentId) {
      setAppointmentDetails(null);
      return;
    }
    apiService
      .getAppointment(quote.appointmentId)
      .then((apt) => {
        if (!apt) {
          setAppointmentDetails(null);
          return;
        }
        const hasFlex = apt.flexScheduledAt?.earliest && apt.flexScheduledAt?.latest;
        const hasExact = apt.scheduledAt;
        if (hasFlex || hasExact) {
          setAppointmentDetails({
            scheduledAt: apt.scheduledAt ?? null,
            flexScheduledAt: apt.flexScheduledAt ?? null,
            status: apt.status ?? null,
          });
        } else {
          setAppointmentDetails({
            scheduledAt: null,
            flexScheduledAt: null,
            status: apt.status ?? null,
          });
        }
      })
      .catch(() => setAppointmentDetails(null));
  }, [quote?.appointmentId]);

  useEffect(() => {
    const urls = quote?.customerUploadedImages;
    if (!quote?.id || !urls || urls.length === 0) {
      setPresignedImageUrls(null);
      return;
    }
    let cancelled = false;
    setPresignedImageUrlsLoading(true);
    apiService
      .getQuotePresignedImageUrls(quote.id)
      .then(({ urls: signed }) => {
        if (!cancelled) {
          setPresignedImageUrls(signed);
          setLoadedImageIndices(new Set());
        }
      })
      .catch(() => {
        if (!cancelled) setPresignedImageUrls([]);
      })
      .finally(() => {
        if (!cancelled) setPresignedImageUrlsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quote?.id]);

  const goBack = () => navigate('/dashboard/business?section=quote-requests');

  const handleCreateAppointment = () => {
    if (quoteId) navigate(`/dashboard/business/quote-requests/${quoteId}/create-appointment`);
  };

  const isLinkedAppointmentCancelled = appointmentDetails?.status === 'CANCELLED';
  const showViewAppointmentButton = Boolean(quote?.appointmentId) && !isLinkedAppointmentCancelled;
  const showCreateAppointmentButton = !quote?.appointmentId || isLinkedAppointmentCancelled;

  const quoteDisplayStatus = quote
    ? getQuoteDisplayStatus({
        ...quote,
        appointmentStatus: appointmentDetails?.status ?? quote.appointmentStatus ?? null,
      })
    : null;

  const handleSaveOfferAndDeposit = async () => {
    if (!quoteId || saving || !quote) return;
    const offerNum = offerAmountInput.trim() === '' ? null : parseFloat(offerAmountInput);
    const depositNum = depositAmountInput.trim() === '' ? null : parseFloat(depositAmountInput);
    const offerExpirationDays = offerExpirationDaysInput.trim() === '' ? 1 : parseInt(offerExpirationDaysInput.trim(), 10);
    if (offerNum !== null && (!Number.isFinite(offerNum) || offerNum < 0)) {
      toast.error('Enter a valid offer amount');
      return;
    }
    if (depositNum !== null && (!Number.isFinite(depositNum) || depositNum < 0)) {
      toast.error('Enter a valid deposit amount');
      return;
    }
    if (!Number.isFinite(offerExpirationDays) || offerExpirationDays < 1) {
      toast.error('Enter a valid number of expiration days');
      return;
    }
    try {
      setSaving(true);
      const result = await apiService.updateQuote(quoteId, {
        businessOfferAmount: offerAmountInput.trim() === '' ? null : offerAmountInput.trim(),
        requiredDepositAmount: depositAmountInput.trim() === '' ? null : depositAmountInput.trim(),
        offerExpirationDays,
      });
      setQuote((prev) =>
        prev
          ? {
              ...prev,
              businessOfferAmount: result.businessOfferAmount,
              requiredDepositAmount: result.requiredDepositAmount,
              offerSentAt: result.offerSentAt ?? prev.offerSentAt ?? null,
              offerExpirationDays: result.offerExpirationDays ?? offerExpirationDays,
              offerExpiredAt: result.offerExpiredAt ?? null,
            }
          : null
      );
      setOfferAmountInput(offerNum != null ? Number(offerNum).toFixed(2) : '');
      setDepositAmountInput(depositNum != null ? depositNum.toFixed(2) : '');
      setOfferExpirationDaysInput(getOfferExpirationDaysInput(result.offerExpirationDays, result.offerExpiredAt));
      toast.success('Offer and deposit saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSendOffer = async () => {
    if (!quoteId || saving || !quote) return;
    const offerNum = offerAmountInput.trim() === '' ? null : parseFloat(offerAmountInput);
    const depositNum = requireDeposit && depositAmountInput.trim() !== '' ? parseFloat(depositAmountInput) : null;
    const offerExpirationDays = offerExpirationDaysInput.trim() === '' ? 1 : parseInt(offerExpirationDaysInput.trim(), 10);
    if (offerNum === null || !Number.isFinite(offerNum) || offerNum < 0) {
      toast.error('Enter a valid total offer amount');
      return;
    }
    if (depositNum !== null && (!Number.isFinite(depositNum) || depositNum < 0)) {
      toast.error('Enter a valid deposit amount');
      return;
    }
    if (!Number.isFinite(offerExpirationDays) || offerExpirationDays < 1) {
      toast.error('Enter a valid number of expiration days');
      return;
    }
    try {
      setSaving(true);
      // Send amounts as strings to avoid float precision loss in JSON (e.g. 100 becoming 99.97)
      const payload: Parameters<typeof apiService.updateQuote>[1] = {
        businessOfferAmount: offerAmountInput.trim() === '' ? null : offerAmountInput.trim(),
        requiredDepositAmount: requireDeposit && depositAmountInput.trim() !== '' ? depositAmountInput.trim() : null,
        offerExpirationDays,
      };
      if (offerMessage.trim()) {
        payload.newMessage = { text: offerMessage.trim() };
      }
      const result = await apiService.updateQuote(quoteId, payload);
      setQuote((prev) =>
        prev
          ? {
              ...prev,
              businessOfferAmount: result.businessOfferAmount,
              requiredDepositAmount: result.requiredDepositAmount,
              offerSentAt: result.offerSentAt ?? prev.offerSentAt ?? null,
              offerExpirationDays: result.offerExpirationDays ?? offerExpirationDays,
              offerExpiredAt: result.offerExpiredAt ?? null,
              status: result.status ?? prev.status,
              conversations: result.conversations ?? prev.conversations,
            }
          : null
      );
      setOfferAmountInput(offerNum != null ? Number(offerNum).toFixed(2) : '');
      setDepositAmountInput(depositNum != null ? depositNum.toFixed(2) : '');
      setOfferExpirationDaysInput(getOfferExpirationDaysInput(result.offerExpirationDays, result.offerExpiredAt));
      if (offerMessage.trim()) setOfferMessage('');
      toast.success('Offer sent to customer');
      navigate('/dashboard/business?section=quote-requests');
    } catch (err) {
      toast.error('Failed to send offer');
    } finally {
      setSaving(false);
    }
  };

  const handleSendMessage = async () => {
    const text = conversationInput.trim();
    if (!quoteId || !text || sendingMessage) return;
    try {
      setSendingMessage(true);
      const result = await apiService.updateQuote(quoteId, { newMessage: { text } });
      setQuote((prev) => (prev ? { ...prev, conversations: result.conversations ?? prev.conversations } : null));
      setConversationInput('');
      toast.success('Message sent');
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const isOfferAccepted =
    quote?.status === 'BUSINESS_OFFER_ACCEPTED' || quote?.status === 'CUSTOMER_OFFER_ACCEPTED';
  const isOfferOrDepositLocked = isOfferAccepted || quote?.status === 'DEPOSIT_RECEIVED';
  const isCurrentOfferExpired =
    quote?.offerExpiredAt != null && !Number.isNaN(new Date(quote.offerExpiredAt).getTime())
      ? new Date(quote.offerExpiredAt).getTime() <= Date.now()
      : false;
  const previewOfferExpirationDays = offerExpirationDaysInput.trim() === '' ? 1 : parseInt(offerExpirationDaysInput.trim(), 10);
  const shouldReuseStoredOfferExpiration =
    Number.isFinite(previewOfferExpirationDays) &&
    previewOfferExpirationDays >= 1 &&
    quote?.offerExpiredAt != null &&
    quote?.offerExpirationDays != null &&
    previewOfferExpirationDays === quote.offerExpirationDays;
  const previewOfferExpirationIso = shouldReuseStoredOfferExpiration
    ? quote?.offerExpiredAt ?? null
    : (
        Number.isFinite(previewOfferExpirationDays) && previewOfferExpirationDays >= 1
          ? getOfferExpirationIsoFromDays(previewOfferExpirationDays)
          : null
      );
  const showOnlineDepositWarning =
    !onlinePaymentsEnabled &&
    (isOfferOrDepositLocked
      ? quote?.requiredDepositAmount != null && Number(quote.requiredDepositAmount) > 0
      : requireDeposit);
  const onlineDepositWarningCard = showOnlineDepositWarning ? (
    <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-amber-900">
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
        <AlertTriangle className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 text-xs leading-relaxed text-amber-900">
        <p className="text-sm font-semibold text-amber-950">Online deposit collection is not enabled</p>
        <p className="mt-1">
          Stripe payments have not been set up for this business. To collect deposits online, go to{' '}
          <Link
            to="/dashboard/business?section=site-config&siteConfigTab=payments"
            className="font-semibold underline"
          >
            Site Configuration &gt; Online Payments
          </Link>{' '}
          and Set Up Payments with Stripe. Until online payments are enabled, once the customer accepts the offer, please coordinate directly with the customer to collect the deposit outside of AACore Booking, such as cash, check, Zelle, Venmo, or another payment method your business accepts.
        </p>
      </div>
    </div>
  ) : null;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr] lg:min-h-screen">
        {shouldShowBusinessDashboard(user) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection="quote-requests"
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            unreadNotificationCount={0}
          />
        )}
        <div className="flex flex-col flex-1 min-w-0">
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button onClick={() => setIsSidebarOpen(true)} className="p-2 rounded-md hover:bg-gray-100">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Quote request details</h1>
              <div className="w-10" />
            </div>
          </div>
          <div className="flex-1 p-4 md:p-8">
            <button onClick={goBack} className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Quote Requests
            </button>
            {loading ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : !quote ? (
              <div className="text-center py-12 text-gray-600">Quote request not found.</div>
            ) : (
              <div className="space-y-6">
                {/* Page header */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-xl md:text-2xl font-bold text-gray-900 uppercase">
                      {formatRequestNumber(quote.id)}
                    </h1>
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${quoteDisplayStatus?.badgeClass ?? 'bg-gray-100 text-gray-800'}`}>
                      {quoteDisplayStatus?.label ?? '—'}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">Submitted {formatDate(quote.createdAt)}</span>
                </div>

                {/* Two-column layout: left = request details, right = offer & actions */}
                <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
                  {/* Left column — request details */}
                  <div className="space-y-5">
                    {/* Requestor */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-500" />
                          Requestor
                        </h2>
                      </div>
                      <div className="p-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Name</label>
                            <p className="mt-1 text-gray-900 font-medium">
                              {[quote.customer_first_name, quote.customer_last_name].filter(Boolean).join(' ') || '—'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Contact</label>
                            <p className="mt-1 text-gray-900 break-all">{quote.customer_email || '—'}</p>
                            <p className="mt-1 text-gray-900">{displayCustomerPhone(quote.customer_phone, canViewFullCustomerPhone(user, currentBusiness?.id)) || '—'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Services */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                          <ClipboardList className="w-4 h-4 text-gray-500" />
                          Services
                        </h2>
                      </div>
                      <div className="p-5">
                        <ul className="space-y-0 divide-y divide-gray-100">
                          {(quote.services || []).map((s) => (
                            <li key={s.id} className="flex justify-between items-center py-3 first:pt-0">
                              <span className="font-medium text-gray-900">{s.name || s.id}</span>
                              <span className="text-gray-600 text-sm tabular-nums">{s.price != null ? formatMoney(s.price) : 'Quotable'}</span>
                            </li>
                          ))}
                          {(!quote.services || quote.services.length === 0) && (
                            <li className="py-3 text-gray-500 text-sm">No services</li>
                          )}
                        </ul>
                      </div>
                    </div>

                    {/* Preferred schedule & customer details */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          Preferred schedule & notes
                        </h2>
                      </div>
                      <div className="p-5 space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Preferred schedule</label>
                          <p className="mt-1 text-gray-900">{formatSchedule(quote.preferredSchedule)}</p>
                        </div>
                        {quote.customerProvidedDetails && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Customer notes</label>
                            <p className="mt-1 text-gray-900 whitespace-pre-wrap text-sm leading-relaxed">{quote.customerProvidedDetails}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Customer uploaded images — above Conversation */}
                    {quote.customerUploadedImages && quote.customerUploadedImages.length > 0 && (
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80">
                          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                            <Image className="w-4 h-4 text-gray-500" />
                            Customer uploaded images
                          </h2>
                        </div>
                        <div className="p-5">
                          {presignedImageUrlsLoading ? (
                            <div className="flex items-center gap-2 text-gray-500 text-sm">
                              <LoadingSpinner size="sm" />
                              Loading images…
                            </div>
                          ) : presignedImageUrls && presignedImageUrls.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {presignedImageUrls.map((url, index) => {
                                const loaded = loadedImageIndices.has(index);
                                return (
                                  <button
                                    key={index}
                                    type="button"
                                    onClick={() => setLightboxImageUrl(url)}
                                    className="relative block w-full rounded-lg overflow-hidden border border-gray-200 aspect-square bg-gray-100 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-1"
                                  >
                                    {!loaded && (
                                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <LoadingSpinner size="sm" />
                                      </div>
                                    )}
                                    <img
                                      src={url}
                                      alt={`Customer image ${index + 1}`}
                                      className="w-full h-full object-cover"
                                      onLoad={() => setLoadedImageIndices((prev) => new Set(prev).add(index))}
                                    />
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm">Unable to load images.</p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                          <History className="w-4 h-4 text-gray-500" />
                          Quote events
                        </h2>
                      </div>
                      <div className="p-5">
                        {quote.quoteEvents && quote.quoteEvents.length > 0 ? (
                          <div className="space-y-3">
                            {quote.quoteEvents.map((event) => (
                              <div key={event.id} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                                <div className="flex items-start justify-between gap-3">
                                  <p className="text-sm font-medium text-gray-900">
                                    {renderQuoteDepositReceivedEventText(
                                      event,
                                      event.eventType === 'DEPOSIT_RECEIVED' && quote.appointmentId
                                        ? () => setIsDepositPaymentDetailsModalOpen(true)
                                        : undefined
                                    )}
                                  </p>
                                  <span className="shrink-0 text-xs text-gray-500">{formatDate(event.createdAt)}</span>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                  {formatQuoteEventActor(event.createdByFirstName, event.createdByLastName)}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No quote events yet.</p>
                        )}
                      </div>
                    </div>

                  </div>

                  {/* Right column — offer & deposit (combined), appointment */}
                  <div className="flex flex-col gap-5 xl:min-h-0">
                    {/* Offer card — stretches on xl to align with Preferred schedule & notes */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden xl:flex-1 xl:flex xl:flex-col xl:min-h-0">
                      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80 flex-shrink-0">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                          <Tag className="w-4 h-4 text-gray-500" />
                          <span className="flex items-center gap-1.5">
                            <span>Offer</span>
                            <span className="normal-case">
                              <InfoTooltip content={OFFER_CARD_TOOLTIP_TEXT} />
                            </span>
                          </span>
                        </h2>
                        <p className="text-xs text-gray-500 mt-1">Set the total amount, optional deposit, add a message, then send to customer.</p>
                      </div>
                      <div className="p-5 space-y-5 flex-1 flex flex-col">
                        {/* Total offer amount */}
                        <div>
                          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                            <span>Total offer amount</span>
                            <span className="normal-case">
                              <InfoTooltip content={TOTAL_OFFER_TOOLTIP_TEXT} />
                            </span>
                          </label>
                          {isOfferOrDepositLocked ? (
                            <p className="text-lg font-semibold text-gray-700 bg-gray-100 px-3 py-2 rounded-lg w-fit">{formatMoney(quote.businessOfferAmount)}</p>
                          ) : (
                            <div className="inline-flex items-center rounded-lg bg-gray-100 px-3 py-2">
                              <span className="text-lg font-semibold text-gray-700">$</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                value={offerAmountInput}
                                onChange={(e) => setOfferAmountInput(e.target.value)}
                                className="w-28 bg-transparent border-0 p-0 text-lg font-semibold text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:outline-none"
                              />
                            </div>
                          )}
                          {!isOfferOrDepositLocked && (
                            <p className="text-xs text-gray-500 mt-1.5">This is the total service price (before any deposit is applied).</p>
                          )}
                        </div>
                        {/* Require deposit toggle + amount */}
                        <div>
                          {!isOfferOrDepositLocked ? (
                            <>
                              <div className="flex items-center justify-between gap-4 mb-2">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Required deposit</span>
                                  <span className="normal-case">
                                    <InfoTooltip content={REQUIRED_DEPOSIT_TOOLTIP_TEXT} />
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={requireDeposit}
                                  onClick={() => setRequireDeposit(!requireDeposit)}
                                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ${
                                    requireDeposit ? 'bg-[#0B4778]' : 'bg-gray-200'
                                  }`}
                                >
                                  <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                                      requireDeposit ? 'translate-x-5' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              </div>
                              <p className="text-xs text-gray-500 mb-2">If enabled, customer pays this amount to accept the offer.</p>
                              {requireDeposit && (
                                <div className="mt-2">
                                  <div className="flex justify-between items-start gap-4">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Deposit amount</label>
                                      <div className="inline-flex items-center rounded-lg bg-gray-100 px-3 py-2">
                                        <span className="text-lg font-semibold text-gray-700">$</span>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          placeholder="0.00"
                                          value={depositAmountInput}
                                          onChange={(e) => setDepositAmountInput(e.target.value)}
                                          className="w-28 bg-transparent border-0 p-0 text-lg font-semibold text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:outline-none"
                                        />
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">DUE NOW</label>
                                      {depositAmountInput.trim() !== '' ? (() => {
                                        const dep = parseFloat(depositAmountInput);
                                        const validDep = Number.isFinite(dep) && dep >= 0;
                                        return (
                                          <>
                                            <p className="text-lg font-semibold text-gray-900">{validDep ? formatMoney(dep) : '—'}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">on acceptance</p>
                                          </>
                                        );
                                      })() : (
                                        <>
                                          <p className="text-lg font-semibold text-gray-900">—</p>
                                          <p className="text-xs text-gray-500 mt-0.5">on acceptance</p>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1.5">Deposit amount is shown to the customer with the quote.</p>
                                  {onlineDepositWarningCard}
                                </div>
                              )}
                              <div className="mt-4">
                                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                                  <span>Offer expiration</span>
                                  <span className="normal-case">
                                    <InfoTooltip content={OFFER_EXPIRATION_TOOLTIP_TEXT} />
                                  </span>
                                </label>
                                <div className="inline-flex w-full overflow-hidden rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-[#0B4778] focus-within:border-[#0B4778]">
                                  <div className="flex-1 min-w-0">
                                    <input
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={offerExpirationDaysInput}
                                      onChange={(e) => setOfferExpirationDaysInput(e.target.value)}
                                      className="w-full bg-transparent border-0 px-3 py-2 text-sm text-gray-900 focus:ring-0 focus:outline-none [appearance:textfield]"
                                    />
                                  </div>
                                  <div className="inline-flex items-center justify-center px-4 border-l border-gray-300 bg-gray-50 text-sm font-medium text-gray-500 whitespace-nowrap">
                                    day(s)
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1.5">The expiration timestamp is calculated from the current time plus this number of days.</p>
                                {isCurrentOfferExpired && (
                                  <p className="text-xs text-amber-700 mt-1.5">This offer is currently expired. Set a new future expiration and send the offer again to let the customer accept it.</p>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="space-y-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Deposit required</label>
                                <p className="text-lg font-semibold text-gray-700 bg-gray-100 px-3 py-2 rounded-lg w-fit">{formatMoney(quote.requiredDepositAmount)}</p>
                              </div>
                              {onlineDepositWarningCard}
                              <div>
                                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Offer expiration</label>
                                <p className="text-sm font-medium text-gray-700 bg-gray-100 px-3 py-2 rounded-lg w-fit">{quote.offerExpiredAt ? formatDate(quote.offerExpiredAt) : '—'}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        {/* Customer will see */}
                        {!isOfferOrDepositLocked && (
                          <div className="rounded-lg p-3 border bg-[#0B4778]/10 border-[#0B4778]/20">
                            <div className="text-sm text-gray-700">
                              <p className="flex items-center gap-1.5">
                                <strong>Customer will see:</strong>
                                <InfoTooltip content={CUSTOMER_WILL_SEE_TOOLTIP_TEXT} />
                              </p>
                              <p className="mt-1">
                                Total: {offerAmountInput.trim() !== '' && Number.isFinite(parseFloat(offerAmountInput)) ? formatMoney(parseFloat(offerAmountInput)) : '—'}
                              </p>
                              <p>
                                {requireDeposit && depositAmountInput.trim() !== '' && Number.isFinite(parseFloat(depositAmountInput)) && parseFloat(depositAmountInput) > 0 ? (
                                  <>Deposit to accept: {formatMoney(parseFloat(depositAmountInput))}</>
                                ) : (
                                  'Deposit to accept: None'
                                )}
                              </p>
                              <p>
                                Expiration: {previewOfferExpirationIso ? formatDate(previewOfferExpirationIso) : '—'}
                              </p>
                            </div>
                          </div>
                        )}
                        {/* Message to customer (optional) */}
                        {!isOfferOrDepositLocked && (
                          <div>
                            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                              <span>Message to customer (optional)</span>
                              <span className="normal-case">
                                <InfoTooltip content={MESSAGE_TO_CUSTOMER_TOOLTIP_TEXT} />
                              </span>
                            </label>
                            <textarea
                              rows={3}
                              placeholder="Add a short note about what is included, timing, or any important details..."
                              value={offerMessage}
                              onChange={(e) => setOfferMessage(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-sm"
                            />
                            <p className="text-xs text-gray-500 mt-1.5">This message will be included with the offer notification.</p>
                          </div>
                        )}
                        {/* Actions: Cancel + Send Offer */}
                        {!isOfferOrDepositLocked && (
                          <div className="flex gap-2 pt-2 mt-auto">
                            <button onClick={goBack} className="flex-1 min-w-0 aacore-btn2-secondary inline-flex items-center justify-center gap-2">
                              <X className="w-4 h-4 flex-shrink-0" />
                              Cancel
                            </button>
                            <button onClick={handleSendOffer} disabled={saving} className="flex-1 min-w-0 aacore-btn2-primary inline-flex items-center justify-center gap-2" style={{ backgroundColor: '#0B4778' }}>
                              <Send className="w-4 h-4 flex-shrink-0" />
                              {saving ? 'Sending...' : quote.status === 'BUSINESS_OFFER_SENT' ? 'Update Offer' : 'Send Offer'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Conversation — in right column under Offer */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-gray-500" />
                          <span className="flex items-center gap-1.5">
                            <span>Conversation</span>
                            <span className="normal-case">
                              <InfoTooltip content={CONVERSATION_TOOLTIP_TEXT} />
                            </span>
                          </span>
                        </h2>
                      </div>
                      <div className="p-5 space-y-4">
                        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                          {[...(quote.conversations || [])].reverse().map((msg: QuoteConversationMessage) => (
                            <div
                              key={msg.id}
                              className={`w-full p-3 rounded-lg ${msg.sender === 'business' ? 'bg-[#0B4778]/10' : 'bg-gray-100'}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <p className="text-sm font-medium text-gray-700">{msg.sender === 'business' ? 'You' : 'Customer'}</p>
                                <p className="text-xs text-gray-500 text-right whitespace-nowrap">{formatDate(msg.createdAt)}</p>
                              </div>
                              <p className="mt-1 text-gray-900 whitespace-pre-wrap">{msg.text}</p>
                            </div>
                          ))}
                          {(!quote.conversations || quote.conversations.length === 0) && (
                            <p className="text-gray-500 text-sm">No messages yet.</p>
                          )}
                        </div>
                        <div className="space-y-3 pt-2 border-t border-gray-100">
                          <textarea
                            placeholder="Reply to customer..."
                            value={conversationInput}
                            onChange={(e) => setConversationInput(e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                          />
                          <button
                            type="button"
                            onClick={handleSendMessage}
                            disabled={!conversationInput.trim() || sendingMessage}
                            className="w-full aacore-btn2-primary inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ backgroundColor: '#0B4778' }}
                          >
                            <Send className="w-4 h-4" />
                            {sendingMessage ? 'Sending...' : 'Send'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Create / View appointment when offer is accepted or an appointment already exists (e.g. after deposit). */}
                    {(quote.status === 'BUSINESS_OFFER_ACCEPTED' || quote.appointmentId) && (
                      <div className="flex-shrink-0">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80">
                            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                              <CalendarPlus className="w-4 h-4 text-gray-500" />
                              Appointment
                            </h2>
                          </div>
                          <div className="p-5 xl:flex-1 xl:flex xl:flex-col xl:justify-end">
                            {appointmentDetails && (appointmentDetails.scheduledAt || (appointmentDetails.flexScheduledAt?.earliest && appointmentDetails.flexScheduledAt?.latest)) && (
                              <div className="mb-4">
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Date & time</p>
                                <p className="mt-1 text-sm text-gray-900 font-medium">
                                  {appointmentDetails.scheduledAt
                                    ? `${formatDateOnly(appointmentDetails.scheduledAt)} ${formatTimeOnly(appointmentDetails.scheduledAt)}`
                                    : appointmentDetails.flexScheduledAt?.earliest && appointmentDetails.flexScheduledAt?.latest
                                      ? `${formatDateOnly(appointmentDetails.flexScheduledAt.earliest)} Between ${formatTimeOnly(appointmentDetails.flexScheduledAt.earliest)} – ${formatTimeOnly(appointmentDetails.flexScheduledAt.latest)}`
                                      : null}
                                </p>
                              </div>
                            )}
                            {isLinkedAppointmentCancelled && (
                              <div className="mb-4">
                                <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200 uppercase tracking-wide">
                                  Cancelled
                                </span>
                              </div>
                            )}
                            {showViewAppointmentButton ? (
                            <Link
                              to={`/appointments/${quote.appointmentId}?from=quote-requests`}
                              className="inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors"
                              style={{ backgroundColor: '#0B4778' }}
                            >
                              <Eye className="w-4 h-4" />
                              View Appointment
                            </Link>
                          ) : showCreateAppointmentButton ? (
                            <button
                              type="button"
                              onClick={handleCreateAppointment}
                              className="inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors"
                              style={{ backgroundColor: '#0B4778' }}
                            >
                              <CalendarPlus className="w-4 h-4" />
                              Create Appointment
                            </button>
                          ) : null}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image lightbox overlay */}
      {lightboxImageUrl && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxImageUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label="View image"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxImageUrl(null); }}
            className="absolute top-4 right-4 p-2.5 rounded-full bg-white text-gray-900 hover:bg-gray-100 shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black/80"
            aria-label="Close"
          >
            <X className="w-6 h-6" strokeWidth={2.5} />
          </button>
          <img
            src={lightboxImageUrl}
            alt="Enlarged view"
            className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <DepositPaymentDetailsModal
        isOpen={isDepositPaymentDetailsModalOpen}
        appointmentId={quote?.appointmentId ?? undefined}
        onClose={() => setIsDepositPaymentDetailsModalOpen(false)}
      />
    </div>
  );
};

export default QuoteRequestDetailsPage;
